/**
 * DealNextAction — a single "what to do next" strip at the top of every deal.
 * First matching rule wins; each rule pairs a message with a one-click action
 * (scroll to the relevant section or open the Calculator).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Lightbulb, ArrowRight } from "lucide-react";

const scrollTo = (selector) => {
  const el = document.querySelector(selector);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

function computeNextAction(deal, invoices, nav, onDraftMilestone) {
  if (["Lost", "Past Lead"].includes(deal.status)) return null;

  const priced = (deal.proposal_option_1 || 0) > 0 || (deal.proposal_option_2 || 0) > 0 ||
    (deal.proposal_option_3 || 0) > 0 || (deal.chosen_amount || 0) > 0;
  const signed = !!deal.scope_signed_at;
  const won = deal.status === "Won" || signed;
  const invTotal = (invoices || []).reduce((s, i) => s + Number(i.total || 0), 0);
  const invPaid = (invoices || []).reduce((s, i) => s + Number(i.amount_paid || 0), 0);
  const outstanding = invTotal - invPaid;

  if (!priced) return {
    text: "Price the job — build the coating system and warranty options.",
    btn: "Open Calculator",
    run: () => nav(`/calculator?deal=${deal.id}`),
  };
  if (!won && !deal.last_scope_sent_at) return {
    text: "Pricing is ready — send the scope/proposal to the customer for signature.",
    btn: "Go to Scope",
    run: () => scrollTo("#deal-group-scope"),
  };
  if (!won) return {
    text: `Scope sent${deal.last_scope_sent_at ? ` on ${deal.last_scope_sent_at.split("T")[0]}` : ""} — awaiting customer signature. Follow up or resend the sign link.`,
    btn: "Review Scope",
    run: () => scrollTo("#deal-group-scope"),
  };
  // Milestone invoicing — when the project hits the next stage, offer a
  // one-click draft invoice for the first Pending milestone (only once all
  // earlier milestones are Paid, so the prompt tracks real progress).
  const ms = deal.payment_milestones || [];
  const nextMsIdx = ms.findIndex((m) => m.status === "Pending");
  const nextMs = nextMsIdx >= 0 ? ms[nextMsIdx] : null;
  const priorSettled = nextMsIdx <= 0 || ms.slice(0, nextMsIdx).every((m) => m.status === "Paid");
  if (nextMs && priorSettled && Number(nextMs.amount || 0) > 0 && onDraftMilestone) return {
    text: `Milestone up next: "${nextMs.label || `#${nextMsIdx + 1}`}" — $${Number(nextMs.amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}${nextMs.percent > 0 ? ` (${nextMs.percent}%)` : ""}. Draft its invoice in one click.`,
    btn: "Draft Invoice",
    run: () => onDraftMilestone(nextMs),
  };
  if ((invoices || []).length === 0) return {
    text: "Deal is won 🎉 — draft the deposit invoice to collect the first payment.",
    btn: "Go to Money",
    run: () => scrollTo("#deal-group-money"),
  };
  if ((deal.payment_milestones || []).length === 0) return {
    text: "Set payment milestones with due dates so nothing slips through billing.",
    btn: "Set Milestones",
    run: () => scrollTo('[data-section="milestones"]') || scrollTo("#deal-group-money"),
  };
  if (!deal.scheduled_start_date) return {
    text: "Schedule the crew — set the start date so the calendar and checklist update.",
    btn: "Go to Execution",
    run: () => scrollTo("#deal-group-execution"),
  };
  if ((deal.material_takeoff || []).length === 0) return {
    text: "Order materials — build the take-off so purchase orders can fire.",
    btn: "Material Take-Off",
    run: () => scrollTo("#deal-group-execution"),
  };
  if (!deal.last_work_order_sent_at) return {
    text: "Assign the subcontractor and send the Work Order for signature.",
    btn: "Go to Execution",
    run: () => scrollTo("#deal-group-execution"),
  };
  if (outstanding > 0.5) return {
    text: `Collect the remaining balance — $${outstanding.toLocaleString(undefined, { maximumFractionDigits: 0 })} outstanding on invoices.`,
    btn: "Go to Money",
    run: () => scrollTo("#deal-group-money"),
  };
  if (!deal.maintenance_plan) return {
    text: "Job is fully collected — sell the Maintenance Plan for recurring revenue.",
    btn: "Maintenance",
    run: () => scrollTo("#deal-group-execution"),
  };
  return {
    text: "All caught up — this project is ready to Mark Complete.",
    btn: null,
    run: null,
  };
}

export default function DealNextAction({ deal, invoices, onDraftMilestone }) {
  const nav = useNavigate();
  const action = computeNextAction(deal, invoices, nav, onDraftMilestone);
  if (!action) return null;

  return (
    <div
      className="mb-4 bg-zinc-950 text-white rounded-sm px-4 py-3 flex items-center gap-3 flex-wrap animate-in fade-in slide-in-from-top-2 duration-300"
      data-testid="deal-next-action"
    >
      <div className="flex items-center gap-2 shrink-0">
        <Lightbulb className="w-4 h-4 text-amber-400" />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400">Next Up</span>
      </div>
      <div className="flex-1 min-w-[200px] text-sm" data-testid="deal-next-action-text">{action.text}</div>
      {action.btn && (
        <button
          onClick={action.run}
          data-testid="deal-next-action-btn"
          className="inline-flex items-center gap-1.5 h-8 px-3 text-[10px] font-bold uppercase tracking-wider bg-white text-zinc-950 hover:bg-amber-400 rounded-sm transition-colors shrink-0"
        >
          {action.btn} <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
